/**
 * @fileoverview Keyboard shortcuts help overlay.
 * Shown via Cmd+/ or ? key. Minimal, non-intrusive.
 */

import { AnimatePresence, motion } from "framer-motion";
import { X, Keyboard } from "lucide-react";
import { Button } from "@/components/ui/button";

interface KeyboardShortcutsHelpProps {
  open: boolean;
  onClose: () => void;
}

const SHORTCUT_GROUPS = [
  {
    title: "Global",
    shortcuts: [
      { keys: ["⌘", "K"], description: "Focus search" },
      { keys: ["⌘", "/"], description: "Toggle this help" },
      { keys: ["?"], description: "Show shortcuts" },
      { keys: ["Esc"], description: "Clear focus / close" },
    ],
  },
];

export function KeyboardShortcutsHelp({ open, onClose }: KeyboardShortcutsHelpProps) {
  return (
    <AnimatePresence>
      {open && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#2d2d2d] border-2 border-white/10 shadow-2xl w-full max-w-sm mx-4 overflow-hidden"
          >
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 className="font-bold text-gray-100 flex items-center gap-2">
                <Keyboard className="w-5 h-5 text-lime-400" />
                Keyboard Shortcuts
              </h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="h-8 w-8 text-gray-400 hover:text-gray-100"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-4 space-y-4">
              {SHORTCUT_GROUPS.map((group) => (
                <div key={group.title}>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                    {group.title}
                  </p>
                  <div className="space-y-2">
                    {group.shortcuts.map((shortcut) => (
                      <div
                        key={shortcut.description}
                        className="flex items-center justify-between"
                      >
                        <span className="text-sm text-gray-300">{shortcut.description}</span>
                        <div className="flex items-center gap-1">
                          {shortcut.keys.map((key) => (
                            <kbd
                              key={key}
                              className="px-2 py-1 bg-white/5 border border-white/10 text-xs font-mono text-gray-300 min-w-[28px] text-center"
                            >
                              {key}
                            </kbd>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="px-4 pb-3">
              <p className="text-xs text-gray-600 text-center">
                Press <kbd className="px-1 py-0.5 bg-white/5 border border-white/10 text-[10px] font-mono">Esc</kbd> to close
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
